import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Minus, Trash2, ScanBarcode, Search, Pause, MessageSquare, PlayCircle, X } from "lucide-react";
import { toast } from "sonner";

export default function POS() {
  const { user, activeBranchId, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [heldBills, setHeldBills] = useState([]);
  const [resumeBillId, setResumeBillId] = useState(null);
  const [cart, setCart] = useState([]);
  const [scan, setScan] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [customerId, setCustomerId] = useState("walkin");
  const [salesmanId, setSalesmanId] = useState("none");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [discountPct, setDiscountPct] = useState("0");
  const [loyaltyRedeem, setLoyaltyRedeem] = useState("0");
  const [paidNow, setPaidNow] = useState("");
  const [creditAmount, setCreditAmount] = useState("");
  const [storeCreditUsed, setStoreCreditUsed] = useState("0");
  const [sendWhatsApp, setSendWhatsApp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orgSettings, setOrgSettings] = useState({ pos_roundoff_enabled: true, pos_roundoff_threshold: 5, pos_roundoff_cap: 5, gst_mode: "excluded", hide_gst_on_customer_bill: false });
  const [roundOffOverride, setRoundOffOverride] = useState(null); // null = auto
  const scanRef = useRef();

  const allowedModes = user.role === "cashier" ? (user.permissions?.payment_modes || ["Cash"]) : ["Cash", "Card", "UPI", "Other", "Full Credit", "Split"];
  const discountCap = user.role === "cashier" ? (user.permissions?.max_discount_percent || 0) : 100;

  const loadHeld = async () => {
    try {
      const { data } = await api.get(`/sales-bills/held${activeBranchId ? `?branch_id=${activeBranchId}` : ""}`);
      setHeldBills(data);
    } catch {}
  };
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get(`/staff/list-active${activeBranchId ? `?branch_id=${activeBranchId}` : ""}`).then((r) => setStaff(r.data)).catch(() => setStaff([]));
    api.get("/organisation/settings").then((r) => setOrgSettings(r.data)).catch(() => {});
    loadHeld();
    scanRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  const refocus = () => setTimeout(() => scanRef.current?.focus(), 50);

  const branchProducts = products.filter((p) => p.branch_id === activeBranchId);
  const branchCustomers = customers.filter((c) => c.branch_id === activeBranchId);

  const addToCart = (product) => {
    if (!product) return;
    setCart((prev) => {
      const idx = prev.findIndex((it) => it.product.id === product.id);
      if (idx >= 0) {
        const next = [...prev];
        if (next[idx].quantity + 1 > (product.branch_stock ?? product.stock)) { toast.error(`Only ${product.branch_stock ?? product.stock} in stock at this branch`); return prev; }
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
        return next;
      }
      if ((product.branch_stock ?? product.stock) < 1) { toast.error("Out of stock at this branch"); return prev; }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const handleScan = async (e) => {
    e.preventDefault();
    const code = scan.trim();
    if (!code) return;
    const local = branchProducts.find((p) => p.barcode === code);
    if (local) { addToCart(local); setScan(""); refocus(); return; }
    try {
      const { data } = await api.get(`/products/barcode/${encodeURIComponent(code)}?branch_id=${activeBranchId}`);
      addToCart(data);
      setProducts((await api.get("/products")).data);
      setScan("");
    } catch { toast.error(`No product with barcode ${code}`); setScan(""); }
    finally { refocus(); }
  };

  const inc = (id) => setCart((prev) => prev.map((it) => {
    if (it.product.id !== id) return it;
    if (it.quantity + 1 > (it.product.branch_stock ?? it.product.stock)) { toast.error(`Only ${it.product.branch_stock ?? it.product.stock} in stock at this branch`); return it; }
    return { ...it, quantity: it.quantity + 1 };
  }));
  const dec = (id) => setCart((prev) => prev.flatMap((it) => it.product.id !== id ? [it] : (it.quantity - 1 <= 0 ? [] : [{ ...it, quantity: it.quantity - 1 }])));
  const removeItem = (id) => setCart((prev) => prev.filter((it) => it.product.id !== id));

  const totals = cart.reduce((a, it) => {
    const gross = it.product.cost_price * (1 + it.product.margin_percent / 100);  // displayed unit price
    const gstPct = it.product.gst_percent || 0;
    let unitBase;
    if (orgSettings.gst_mode === "included" && gstPct > 0) {
      // gross is inclusive → back out unit_base
      unitBase = gross / (1 + gstPct / 100);
    } else {
      unitBase = gross;
    }
    const lineBase = unitBase * it.quantity;
    const gst = lineBase * gstPct / 100;
    a.subtotal += lineBase; a.cgst += gst / 2; a.sgst += gst / 2;
    return a;
  }, { subtotal: 0, cgst: 0, sgst: 0 });
  const preDiscount = totals.subtotal + totals.cgst + totals.sgst;
  const discountValue = preDiscount * (parseFloat(discountPct) || 0) / 100;
  const loyaltyValue = loyaltyRedeem; // assume 1pt = ₹1 for display; actual value decided server-side
  const grandTotalBeforeRoundoff = Math.max(0, preDiscount - discountValue - loyaltyValue);

  // Round-off (org rule unless manually overridden)
  const computeAutoRoundoff = (gt) => {
    if (!orgSettings.pos_roundoff_enabled || gt <= 0) return 0;
    const threshold = orgSettings.pos_roundoff_threshold || 5;
    const cap = orgSettings.pos_roundoff_cap || 5;
    const base = Math.floor(gt / 10) * 10;
    const lastDigit = gt - base;
    if (lastDigit === 0) return 0;
    const target = lastDigit >= threshold ? base + 10 : base;
    const adj = Math.round((target - gt) * 100) / 100;
    return Math.abs(adj) > cap ? 0 : adj;
  };
  const autoRoundoff = computeAutoRoundoff(grandTotalBeforeRoundoff);
  const roundOffValue = roundOffOverride !== null ? parseFloat(roundOffOverride) || 0 : autoRoundoff;
  const grandTotal = Math.max(0, grandTotalBeforeRoundoff + roundOffValue);

  const currentCustomer = branchCustomers.find((c) => c.id === customerId);

  const checkout = async (isHold = false) => {
    if (cart.length === 0) { toast.error("Cart is empty"); return; }
    setSubmitting(true);
    try {
      // Build payment payload
      const isFullCredit = paymentMethod === "Full Credit";
      const isSplit = paymentMethod === "Split";
      let paid_now = grandTotal;
      let credit_amount = 0;
      let actual_payment_method = paymentMethod;
      if (isFullCredit) {
        paid_now = 0;
        credit_amount = grandTotal;
      } else if (isSplit) {
        paid_now = parseFloat(paidNow) || 0;
        credit_amount = parseFloat(creditAmount) || 0;
        actual_payment_method = "Split";
        const sc = parseFloat(storeCreditUsed) || 0;
        if (Math.abs(paid_now + credit_amount + sc - grandTotal) > 0.01) {
          toast.error(`Paid Now + Credit + Store Credit must equal ${formatINR(grandTotal)}`);
          setSubmitting(false);
          return;
        }
      }
      const { data } = await api.post("/sales-bills", {
        branch_id: activeBranchId,
        pos_terminal_id: user.pos_terminal_id || null,
        customer_id: customerId === "walkin" ? null : customerId,
        items: cart.map((it) => ({ product_id: it.product.id, quantity: it.quantity })),
        discount_percent: parseFloat(discountPct) || 0,
        discount_amount: 0,
        loyalty_redeem_points: parseInt(loyaltyRedeem) || 0,
        payment_method: actual_payment_method,
        paid_now, credit_amount,
        store_credit_used: parseFloat(storeCreditUsed) || 0,
        on_hold: isHold,
        send_whatsapp: sendWhatsApp,
        resume_bill_id: resumeBillId,
        salesman_id: salesmanId === "none" ? null : salesmanId,
        salesman_name: salesmanId === "none" ? null : (staff.find((s) => s.id === salesmanId)?.full_name || null),
        round_off: isHold ? 0 : roundOffValue,
      });
      toast.success(isHold ? `Bill held: ${data.bill_number}` : `Bill ${data.bill_number} generated`);
      if (!isHold) navigate(`/bills/${data.id}`);
      else {
        clearActiveBill();
        loadHeld();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const clearActiveBill = () => {
    setCart([]);
    setLoyaltyRedeem("0");
    setDiscountPct("0");
    setCustomerId("walkin");
    setSalesmanId("none");
    setResumeBillId(null);
    setSendWhatsApp(false);
    setPaidNow("");
    setCreditAmount("");
    setStoreCreditUsed("0");
    setRoundOffOverride(null);
  };

  const resumeHeld = (bill) => {
    // Map held bill items back to live product cart
    const newCart = [];
    for (const it of bill.items || []) {
      const prod = products.find((p) => p.id === it.product_id);
      if (prod) newCart.push({ product: prod, quantity: it.quantity });
    }
    setCart(newCart);
    setCustomerId(bill.customer_id || "walkin");
    setDiscountPct(String(bill.discount_percent || 0));
    setLoyaltyRedeem(String(bill.loyalty_redeemed_points || 0));
    setPaymentMethod(bill.payment_method || "Cash");
    setResumeBillId(bill.id);
    toast.info(`Resumed ${bill.bill_number}`);
    refocus();
  };

  const filtered = searchQuery ? branchProducts.filter((p) => [p.name, p.barcode, p.category].some((v) => (v || "").toLowerCase().includes(searchQuery.toLowerCase()))).slice(0, 8) : [];

  return (
    <div className="h-screen flex flex-col bg-slate-50" data-testid="pos-terminal">
      <div className="h-14 border-b border-slate-200 bg-white flex items-center justify-between px-6 flex-shrink-0">
        <div className="flex items-center gap-2"><ScanBarcode className="w-5 h-5 text-slate-900" /><div className="font-semibold">POS Terminal</div>
          {resumeBillId && (
            <span className="ml-3 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300" data-testid="pos-resume-indicator">
              Resuming hold
              <button type="button" onClick={() => { setResumeBillId(null); toast.info("Resume cleared"); }} className="ml-1 hover:text-amber-900"><X className="w-3 h-3" /></button>
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {heldBills.length > 0 && (
            <div className="flex items-center gap-1 max-w-[60vw] overflow-x-auto" data-testid="pos-held-strip">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 mr-1">Held ({heldBills.length}):</span>
              {heldBills.map((b) => (
                <button key={b.id} type="button" onClick={() => resumeHeld(b)}
                  className={`flex items-center gap-1 px-2 py-1 text-xs border rounded-none whitespace-nowrap ${resumeBillId === b.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 hover:bg-slate-50"}`}
                  data-testid={`pos-held-${b.id}`}>
                  <PlayCircle className="w-3 h-3" />
                  <span className="font-mono">{b.bill_number.split("-").pop()}</span>
                  <span className="text-[10px] text-slate-500">{b.customer_name}</span>
                </button>
              ))}
            </div>
          )}
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{new Date().toLocaleTimeString("en-IN")}</div>
        </div>
      </div>
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 overflow-hidden">
        <div className="lg:col-span-8 border-r border-slate-200 bg-white flex flex-col overflow-hidden">
          <form onSubmit={handleScan} className="p-4 border-b border-slate-200 flex gap-2">
            <Input ref={scanRef} value={scan} onChange={(e) => setScan(e.target.value)} autoFocus placeholder="Scan or type barcode..." className="h-14 text-xl font-mono tracking-widest border-2 rounded-none focus-visible:ring-blue-500" data-testid="pos-barcode-input" />
            <Button type="submit" className="h-14 px-6 bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="pos-scan-btn"><ScanBarcode className="w-5 h-5" /></Button>
          </form>
          <div className="px-4 py-3 border-b border-slate-200 relative">
            <div className="flex items-center gap-2"><Search className="w-4 h-4 text-slate-400" /><Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search products..." className="h-9 rounded-none border-slate-300" data-testid="pos-search" /></div>
            {filtered.length > 0 && (
              <div className="absolute left-4 right-4 top-full mt-1 bg-white border border-slate-200 shadow-lg z-20 max-h-72 overflow-auto">
                {filtered.map((p) => (
                  <button key={p.id} type="button" onClick={() => { addToCart(p); setSearchQuery(""); refocus(); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 flex justify-between items-center" data-testid={`pos-search-${p.id}`}>
                    <div><div className="font-medium text-sm">{p.name}</div><div className="text-xs text-slate-500 font-mono">{p.barcode}</div></div>
                    <div className="text-right"><div className="font-semibold text-sm">{formatINR(p.selling_price)}</div><div className="text-xs text-slate-500">Stock: {p.branch_stock ?? p.stock}</div></div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex-1 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10"><tr><th className="px-4 py-3 text-left font-semibold">Item</th><th className="px-4 py-3 text-right font-semibold">MRP</th><th className="px-4 py-3 text-right font-semibold">Price</th><th className="px-4 py-3 text-center font-semibold">Qty</th><th className="px-4 py-3 text-right font-semibold">Total</th><th /></tr></thead>
              <tbody>
                {cart.length === 0 && <tr><td colSpan={6} className="px-4 py-24 text-center text-slate-400"><ScanBarcode className="w-10 h-10 mx-auto mb-3 opacity-40" />Scan a barcode or search.</td></tr>}
                {cart.map((it) => {
                  const gstPct = it.product.gst_percent || 0;
                  // Effective price the customer pays per unit (GST-inclusive)
                  const grossUnit = it.product.cost_price * (1 + it.product.margin_percent / 100);
                  const effPrice = orgSettings.gst_mode === "included" ? grossUnit : grossUnit * (1 + gstPct / 100);
                  const line = effPrice * it.quantity;
                  const mrp = parseFloat(it.product.mrp || 0);
                  const fd = parseFloat(it.product.fake_discount_percent || 0);
                  const hasDiscount = mrp > 0 && mrp > effPrice + 0.01;
                  return (
                    <tr key={it.product.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cart-row-${it.product.id}`}>
                      <td className="px-4 py-3"><div className="font-medium">{it.product.name}</div><div className="text-xs text-slate-500 font-mono">{it.product.barcode}</div></td>
                      <td className="px-4 py-3 text-right">
                        {mrp > 0 ? (
                          <div className="flex flex-col items-end">
                            <span className={hasDiscount ? "line-through text-slate-400 text-xs tabular-nums" : "tabular-nums"}>{formatINR(mrp)}</span>
                            {hasDiscount && fd > 0 && <span className="text-[10px] text-green-700 font-semibold">{fd.toFixed(0)}% off</span>}
                          </div>
                        ) : "-"}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium">{formatINR(effPrice)}</td>
                      <td className="px-4 py-3"><div className="flex items-center justify-center gap-1"><Button type="button" size="icon" variant="ghost" className="h-7 w-7 rounded-none" onClick={() => dec(it.product.id)} data-testid={`cart-dec-${it.product.id}`}><Minus className="w-3 h-3" /></Button><span className="w-8 text-center font-semibold tabular-nums">{it.quantity}</span><Button type="button" size="icon" variant="ghost" className="h-7 w-7 rounded-none" onClick={() => inc(it.product.id)} data-testid={`cart-inc-${it.product.id}`}><Plus className="w-3 h-3" /></Button></div></td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatINR(line)}</td>
                      <td className="px-4 py-3 text-right"><Button type="button" size="icon" variant="ghost" className="h-7 w-7 rounded-none text-red-600" onClick={() => removeItem(it.product.id)} data-testid={`cart-remove-${it.product.id}`}><Trash2 className="w-3.5 h-3.5" /></Button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="lg:col-span-4 bg-slate-50 flex flex-col overflow-hidden">
          <div className="p-5 space-y-4 flex-1 overflow-auto">
            <div><div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Customer</div>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="rounded-none bg-white" data-testid="pos-customer"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="walkin">Walk-in Customer</SelectItem>{branchCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.phone} · {c.loyalty_points || 0}pt</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Salesman</div>
              <Select value={salesmanId} onValueChange={setSalesmanId}>
                <SelectTrigger className="rounded-none bg-white" data-testid="pos-salesman"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None / Unassigned</SelectItem>
                  {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}{s.role_title ? ` · ${s.role_title}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Payment</div>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="rounded-none bg-white" data-testid="pos-payment"><SelectValue /></SelectTrigger>
                <SelectContent>{allowedModes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {paymentMethod === "Full Credit" && currentCustomer && (
              <div className="bg-orange-50 border border-orange-200 p-3 text-xs">
                <div className="font-semibold text-orange-900 mb-1">Full Credit Sale</div>
                <div className="text-orange-800">All ₹{grandTotal.toFixed(2)} will be added to <b>{currentCustomer.name}</b>'s outstanding credit.</div>
                <div className="text-orange-700 mt-1">Credit limit: ₹{(currentCustomer.credit_limit || 0).toFixed(0)}</div>
              </div>
            )}
            {paymentMethod === "Split" && currentCustomer && (
              <div className="bg-blue-50 border border-blue-200 p-3 space-y-2" data-testid="split-payment-form">
                <div className="font-semibold text-blue-900 text-xs uppercase tracking-widest">Split Payment</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Paid Now</Label>
                    <Input type="number" min="0" max={grandTotal} step="0.01" value={paidNow}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPaidNow(v);
                        const n = parseFloat(v) || 0;
                        const sc = parseFloat(storeCreditUsed) || 0;
                        setCreditAmount(String(Math.max(0, grandTotal - n - sc).toFixed(2)));
                      }}
                      className="rounded-none bg-white" data-testid="pos-paid-now" />
                  </div>
                  <div>
                    <Label className="text-xs">Credit</Label>
                    <Input type="number" min="0" max={grandTotal} step="0.01" value={creditAmount}
                      onChange={(e) => {
                        const v = e.target.value;
                        setCreditAmount(v);
                        const n = parseFloat(v) || 0;
                        const sc = parseFloat(storeCreditUsed) || 0;
                        setPaidNow(String(Math.max(0, grandTotal - n - sc).toFixed(2)));
                      }}
                      className="rounded-none bg-white" data-testid="pos-credit-amount" />
                  </div>
                </div>
                <div className="text-[10px] text-slate-500">Total = Paid Now + Credit{(parseFloat(storeCreditUsed) || 0) > 0 ? " + Store Credit" : ""}</div>
              </div>
            )}
            {currentCustomer && (currentCustomer.store_credit || 0) > 0 && (
              <div>
                <Label className="text-xs">Store Credit (avail: {formatINR(currentCustomer.store_credit)})</Label>
                <Input type="number" min="0" max={currentCustomer.store_credit} step="0.01" value={storeCreditUsed}
                  onChange={(e) => setStoreCreditUsed(e.target.value)}
                  className="rounded-none bg-white mt-1" data-testid="pos-store-credit" />
              </div>
            )}
            {hasPerm("discount_apply") && <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Discount % (cap {discountCap}%)</div>
              <Input type="number" min={0} max={discountCap} step="0.1" value={discountPct}
                onFocus={(e) => { if (e.target.value === "0") setDiscountPct(""); }}
                onBlur={(e) => { if (e.target.value === "") setDiscountPct("0"); }}
                onChange={(e) => setDiscountPct(e.target.value.replace(/^0+(?=\d)/, ""))}
                className="rounded-none bg-white" data-testid="pos-discount" />
            </div>}
            {hasPerm("loyalty_redeem") && currentCustomer && (currentCustomer.loyalty_points || 0) > 0 && (
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Redeem points (avail: {currentCustomer.loyalty_points})</div>
                <Input type="number" min={0} max={currentCustomer.loyalty_points} value={loyaltyRedeem}
                  onFocus={(e) => { if (e.target.value === "0") setLoyaltyRedeem(""); }}
                  onBlur={(e) => { if (e.target.value === "") setLoyaltyRedeem("0"); }}
                  onChange={(e) => setLoyaltyRedeem(e.target.value.replace(/^0+(?=\d)/, ""))}
                  className="rounded-none bg-white" data-testid="pos-loyalty-redeem" />
              </div>
            )}
            {(user.role !== "cashier" || hasPerm("whatsapp_bill_toggle")) && currentCustomer && (
              <div className="flex items-center justify-between bg-white border border-slate-200 p-3">
                <div className="flex items-center gap-2 text-sm"><MessageSquare className="w-4 h-4 text-green-600" />Send bill on WhatsApp</div>
                <Switch checked={sendWhatsApp} onCheckedChange={setSendWhatsApp} data-testid="pos-whatsapp-toggle" />
              </div>
            )}
            <div className="border-t border-slate-200 pt-4 space-y-2 text-sm">
              {/* Compute MRP / savings totals for the cart */}
              {(() => {
                const mrpTotal = cart.reduce((s, it) => s + (parseFloat(it.product.mrp || 0) * it.quantity), 0);
                const gstPctW = (it) => it.product.gst_percent || 0;
                const eff = cart.reduce((s, it) => {
                  const grossUnit = it.product.cost_price * (1 + it.product.margin_percent / 100);
                  const eu = orgSettings.gst_mode === "included" ? grossUnit : grossUnit * (1 + gstPctW(it) / 100);
                  return s + eu * it.quantity;
                }, 0);
                const savings = Math.max(0, mrpTotal - eff);
                if (mrpTotal <= 0 || savings <= 0.01) return null;
                return (
                  <>
                    <div className="flex justify-between"><span className="text-slate-600">Total MRP</span><span className="tabular-nums line-through text-slate-400">{formatINR(mrpTotal)}</span></div>
                    <div className="flex justify-between text-green-700 font-semibold"><span>You Save</span><span className="tabular-nums">- {formatINR(savings)}</span></div>
                  </>
                );
              })()}
              {!orgSettings.hide_gst_on_customer_bill && <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="tabular-nums">{formatINR(totals.subtotal)}</span></div>}
              {!orgSettings.hide_gst_on_customer_bill && <div className="flex justify-between"><span className="text-slate-600">CGST</span><span className="tabular-nums" data-testid="pos-cgst">{formatINR(totals.cgst)}</span></div>}
              {!orgSettings.hide_gst_on_customer_bill && <div className="flex justify-between"><span className="text-slate-600">SGST</span><span className="tabular-nums" data-testid="pos-sgst">{formatINR(totals.sgst)}</span></div>}
              {discountValue > 0 && <div className="flex justify-between text-slate-600"><span>Discount</span><span className="tabular-nums">- {formatINR(discountValue)}</span></div>}
              {loyaltyValue > 0 && <div className="flex justify-between text-slate-600"><span>Loyalty</span><span className="tabular-nums">- {formatINR(loyaltyValue)}</span></div>}
              {/* Editable round-off line */}
              {orgSettings.pos_roundoff_enabled && (
                <div className="flex justify-between items-center pt-1 border-t border-dashed border-slate-200">
                  <span className="text-slate-600">Round-off {roundOffOverride !== null && <span className="text-[10px] text-amber-600">(manual)</span>}</span>
                  <div className="flex items-center gap-1">
                    <Input type="number" step="0.5" value={roundOffOverride !== null ? roundOffOverride : autoRoundoff.toFixed(2)}
                      onChange={(e) => setRoundOffOverride(e.target.value)}
                      className="rounded-none bg-white w-20 h-7 text-right text-xs tabular-nums" data-testid="pos-roundoff" />
                    {roundOffOverride !== null && (
                      <button type="button" onClick={() => setRoundOffOverride(null)} className="text-[10px] text-slate-500 hover:text-slate-900" data-testid="pos-roundoff-reset">auto</button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="border-t-2 border-slate-900 pt-4">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Grand Total</div>
              <div className="text-5xl font-black tracking-tighter tabular-nums mt-1" data-testid="pos-grand-total" style={{ fontFamily: "Cabinet Grotesk, IBM Plex Sans, sans-serif" }}>{formatINR(grandTotal)}</div>
            </div>
          </div>
          <div className="p-4 border-t border-slate-200 bg-white space-y-2">
            <Button onClick={() => checkout(false)} disabled={submitting || cart.length === 0} className="w-full bg-green-600 hover:bg-green-700 text-white text-lg py-6 rounded-none font-bold" data-testid="pos-checkout-btn">{submitting ? "Processing..." : `Charge ${formatINR(grandTotal)}`}</Button>
            <div className="flex gap-2">
              {hasPerm("bill_hold") && <Button variant="outline" onClick={() => checkout(true)} disabled={submitting || cart.length === 0} className="flex-1 rounded-none" data-testid="pos-hold-btn"><Pause className="w-4 h-4 mr-1" />Hold</Button>}
              <Button variant="outline" onClick={clearActiveBill} disabled={cart.length === 0} className="flex-1 rounded-none" data-testid="pos-clear-btn">Clear</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
